/**
 * parseRequestParams — NextRequest의 searchParams를 Zod 스키마로 파싱
 *
 * - URLSearchParams → plain object 변환
 * - bracket 표기법("options[progress]")을 그대로 Zod 키로 사용 가능
 * - 유효성 실패 시 status 400 에러를 throw (apiWrapper의 errorHandler가 처리)
 *
 * @example
 * const schema = baseQuerySchema.extend({
 *   "options[progress]": z.string().trim().optional(),
 * }).transform((d) => ({
 *   ...d,
 *   progress: d["options[progress]"],
 * }));
 *
 * const data = parseRequestParams(req, schema);
 * // data.keyword, data.progress, data.page ...
 */

import { type NextRequest } from "next/server";
import { z } from "zod";

export function parseRequestParams<T extends z.ZodTypeAny>(
    req: NextRequest,
    schema: T
): z.infer<T> {
    const { searchParams } = new URL(req.url);
    const raw = Object.fromEntries(searchParams);

    const result = schema.safeParse(raw);
    if (!result.success) {
        const first = result.error.issues[0];
        const message = first
            ? `${first.path.join(".") || "파라미터"}: ${first.message}`
            : "유효하지 않은 요청 파라미터입니다.";

        // errorHandler의 isAppError 조건(code + status)을 만족하도록 설정
        const err = new Error(message) as Error & { status: number; code: string };
        err.status = 400;
        err.code = "BAD_REQUEST";
        throw err;
    }

    return result.data;
}
